/**
 * Customer dashboard listing the signed-in user's model report orders, with
 * search, pagination, payment and document downloads.
 */
import React, { useState, useEffect, useLayoutEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useAuth, supabase } from "../context/AuthContext";
import useHighlightOrderId from "../hooks/useHighlightOrderId";
import { convertToMelbourneDate } from "../utils/dateUtils";
import {
  FaFileInvoiceDollar,
  FaFileAlt,
  FaFilePdf,
} from "react-icons/fa";
import CustomerLayout from './CustomerLayout';
import ArchiveConfirmModal from './archive/ArchiveConfirmModal';
import StatusToast from './StatusToast';
import { archiveOrder, canArchiveOrderStatus } from '../utils/archiveOrderApi';
import { ChevronLeftIcon, ChevronRightIcon, ChevronDownIcon, ChevronUpIcon, EllipsisVerticalIcon, ArchiveBoxIcon } from "@heroicons/react/24/outline";

const CUSTOMER_DASHBOARD_PAGE_KEY = 'customer-dashboard-current-page';

// Page number survives reloads and back-navigation via sessionStorage; falls back to page 1.
const readStoredDashboardPage = () => {
  if (typeof window === 'undefined') {
    return 1;
  }

  const parsed = Number.parseInt(
    window.sessionStorage.getItem(CUSTOMER_DASHBOARD_PAGE_KEY) || '1',
    10
  );

  return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
};

const isDocumentReload = () => {
  if (typeof performance === 'undefined' || !performance.getEntriesByType) {
    return false;
  }

  return performance.getEntriesByType('navigation')[0]?.type === 'reload';
};

// Terms and Conditions Modal Component
// Deed of License the customer must tick before an order is allowed to proceed.
const TermsAndConditionsModal = ({ isOpen, onClose, onAccept, orderId }) => {
  const [isChecked, setIsChecked] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setIsChecked(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/28 px-4 py-6 backdrop-blur-[3px]">
      <div
        className="flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-[1rem] border border-slate-300 bg-white ring-1 ring-slate-200/80 shadow-[0_24px_64px_-28px_rgba(15,23,42,0.42)]"
        role="dialog"
        aria-modal="true"
        aria-labelledby="deed-of-license-title"
      >
        <div className="shrink-0 border-b border-slate-200 px-6 py-4">
          <h2 id="deed-of-license-title" className="text-lg font-semibold text-slate-900">
            DEED OF LICENSE
          </h2>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
          <div className="prose prose-sm text-sm">
            <p className="mb-4 text-sm">
              This Deed is made between Model Report Author i.e, ADR Model Reports Pty Ltd (ACN-634 641 811) or Shui Mir Pty Ltd (ACN- 663 560 110), hereinafter called "the Owner" of the One Part and the Registered Automotive Workshop referred to in item one of the First Schedule to this Deed hereinafter called "the Customer" of the Other Part.
            </p>

            <h3 className="text-base font-semibold mb-2">RECITALS:</h3>
            <p className="mb-2 text-sm">A. The Owner is the Owner of the Model Reports.</p>
            <p className="mb-2 text-sm">B. The Owner will provide to the Customer a Model Reports upon completion of the works for which the Owner was engaged by the Customer.</p>
            <p className="mb-4 text-sm">C. The Customer has agreed to accept delivery of the Model Reports on terms specified in this Deed.</p>

            <h3 className="text-base font-semibold mb-2">OPERATIVE PART:</h3>
            <p className="mb-4 text-sm">On the terms and conditions set out below the Owner gives to the Customer a Model Reports:</p>

            <ol className="list-decimal pl-6 space-y-1 text-sm">
              <li>The Customer acknowledges that the contents of the Model Reports belong to the Owner and that the Owner has exclusive right of ownership of the Model Reports.</li>
              <li>The Customer has met full payment of his/her/its financial obligations to the Owner prior to the delivery of the Model Reports to the Customer.</li>
              <li>The Customer has signed a Deed of License.</li>
              <li>The Customer will use the Model Reports for purposes only of demonstrating to Vehicle Safety Standards staff the evidence of compliance of the vehicle and/or component to the standard required in "Motor Vehicle Standards (Approval to Place Used Import Plates) Determination 2006 No.1" and as amended from time to time, to meet audit requirements, to provide instruction to employees of the Customer on how best to comply the vehicle, to assist in production of Vehicle Inspection Certificates and Consumer Information Notices for vehicles plated by the Customer, or as otherwise required expressly by the Motor Vehicles Standards Act, its Regulations and Determinations.</li>
              <li>The Customer will not permit any other person, group, legal entity, whatsoever, duplicate to have access whether by direct or indirect means to the Model Reports in whole or in part, other than the Vehicle Safety Standards staff, auditors and employees of the Registered Automotive Workshop.</li>
              <li>The Customer will not permit the contents or any part thereof of the Model Reports to be read, copied, reproduced in any manner whatsoever other than the VIN allocated to the vehicle in the limited circumstances set out above. The Customer accepts full responsibility ensuring the use of Model Reports with the allocated Unique ID.</li>
              <li>The Customer agrees that in the event of dissolution or de-registration of the Registered Automotive Workshop by Vehicle Safety Standards, the complete Model Reports and any copies shall within 14 days thereof be returned to the licensee by Registered Post.</li>
              <li>The Customer acknowledges that failure to observe any of the terms set out above will place the Customer in breach of this Deed and face penalties.</li>
              <li>In circumstances where the Customer has breached this Deed the Owner,
                <ol className="list-decimal pl-6 mt-2 text-sm">
                  <li>may request the immediate compensation of the Model Reports and any copies thereof in its entirety;</li>
                  <li>advise Vehicle Safety Standards of breach of Deed pursuant to the Customer being a Fit and Proper person;</li>
                  <li>sue for damages for loss suffered by the Owner as a consequence whether directly or indirectly through the actions of the Customer.</li>
                </ol>
              </li>
              <li>The Model Reports is supplied "as is" to meet evidence of compliance requirements current at the date of this agreement. The Customer accepts and acknowledges that any changes to the above requirements are beyond the control of the Owner, and that the Owner is under no obligation or duty to provide information, either free or at additional cost, with regard to any possible future changes to these requirements. The Customer accepts any and all risk associated with any future change to regulations and/or their enforcement.</li>
            </ol>

            <p className="mt-4 text-sm">
              Note: The Unique Id issued to the work instruction and the checklist as per regulations is authorised to use as stated above (to the unique VIN) to insure the vehicle is complied and modified in accordance to the rules and regulations set by the department of transport in particular RVSA (Road Vehicle Standards Act).
            </p>
          </div>

          <div className="mt-6 flex items-center">
            <input
              type="checkbox"
              id="terms-checkbox"
              checked={isChecked}
              onChange={(e) => setIsChecked(e.target.checked)}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label
              htmlFor="terms-checkbox"
              className="ml-2 block text-sm text-gray-900"
            >
              I have read and agree to the terms and conditions
            </label>
          </div>
        </div>

        <div className="flex shrink-0 justify-end gap-3 border-t border-slate-200 bg-slate-50/70 px-6 py-4">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onAccept(orderId)}
            disabled={!isChecked}
            className={`px-4 py-2 text-sm font-medium text-white rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
              isChecked
                ? "bg-blue-600 hover:bg-blue-700"
                : "bg-blue-300 cursor-not-allowed"
            }`}
          >
            Accept & Continue
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

const NoOrdersDisplay = () => (
  <div className="text-center py-12">
    <div className="mx-auto h-24 w-24 text-gray-400">
      <svg
        className="h-20 w-20"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        aria-hidden="true"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          d="M9 13h6m-3-3v6m5 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
        />
      </svg>
    </div>
    <h3 className="mt-4 text-lg font-medium text-gray-900">No Orders Yet</h3>
    <p className="mt-2 text-sm text-gray-500">
      To begin, create your first order by clicking the "New Order" button in side panel.
    </p>
  </div>
);

const NoMatchingOrdersDisplay = () => (
  <div className="text-center py-12">
    <div className="mx-auto h-24 w-24 text-gray-400">
      <svg
        className="h-20 w-20"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        aria-hidden="true"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
        />
      </svg>
    </div>
    <h3 className="mt-4 text-lg font-medium text-gray-900">No Matching Orders</h3>
    <p className="mt-2 text-sm text-gray-500">
      No orders match your current search. Try a different filter or clear your search.
    </p>
  </div>
);

// Blanks and the "-" / "- -" table placeholders are not copyable.
const isNonCopyableValue = (value) => {
  if (value === null || value === undefined) return true;
  const normalized = String(value).trim();
  return normalized === "" || normalized === "-" || normalized === "- -";
};

// Click-to-copy wrapper for identifiers (VIN, unique ID, invoice number) shown in table.
const CopyableText = ({ value, className = "" }) => {
  const [copied, setCopied] = useState(false);
  const copiedTimeoutRef = useRef(null);
  const displayValue = isNonCopyableValue(value) ? (String(value ?? "-").trim() || "-") : String(value);

  useEffect(() => {
    return () => {
      if (copiedTimeoutRef.current) {
        window.clearTimeout(copiedTimeoutRef.current);
      }
    };
  }, []);

  // Placeholders like "-" / "- -" stay plain text so row expand/collapse still works
  if (isNonCopyableValue(value)) {
    return <span className={className}>{displayValue}</span>;
  }

  const handleCopy = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    try {
      await navigator.clipboard.writeText(displayValue);
      setCopied(true);
      if (copiedTimeoutRef.current) {
        window.clearTimeout(copiedTimeoutRef.current);
      }
      copiedTimeoutRef.current = window.setTimeout(() => setCopied(false), 1000);
    } catch (error) {
      console.error("Failed to copy text:", error);
    }
  };

  return (
    <span className="relative inline-flex max-w-full">
      {copied && (
        <span className="pointer-events-none absolute bottom-full left-1/2 z-20 mb-1.5 -translate-x-1/2 whitespace-nowrap rounded-md bg-slate-800 px-2 py-1 text-xs font-medium text-white shadow-sm">
          Copied!
        </span>
      )}
      <span
        role="button"
        tabIndex={0}
        onClick={handleCopy}
        onMouseDown={(event) => {
          // Keep row toggle from firing alongside copy
          event.stopPropagation();
        }}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            handleCopy(event);
          }
        }}
        className={`max-w-full cursor-copy rounded-md px-1.5 py-0.5 transition-colors ${
          copied
            ? "bg-blue-100 text-slate-800"
            : "hover:bg-blue-50"
        } ${className}`}
        title="Click to copy"
      >
        {displayValue}
      </span>
    </span>
  );
};

// Update the OrderActions component
const OrderActions = ({
  order,
  onDownloadInvoice,
  onDownloadReceipt,
  onDownloadDocuments,
  onArchive,
  archiveDisabled = false,
  downloadingInvoice,
  downloadingReceipt,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef(null);
  // Invoice unlocks once a transaction exists (Payment Pending / Paid).
  // Receipts and report documents stay locked until payment clears.
  const isPaid = order.status === "paid";
  const hasInvoice =
    order.status === "paid" || order.status === "payment pending";
  const isInvoiceDisabled = !hasInvoice || downloadingInvoice[order.id];
  const isReceiptDisabled = !isPaid || downloadingReceipt[order.id];
  const isDocumentsDisabled = !isPaid;
  const canArchive = canArchiveOrderStatus(order.status) && !archiveDisabled;

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  const closeMenu = () => setIsOpen(false);

  return (
    <>
      {/* Desktop: 3 icon buttons */}
      <div className="relative hidden items-center justify-center gap-3 md:flex">
        <button
          onClick={(e) => {
            e.stopPropagation();
            onDownloadInvoice(order.id);
          }}
          disabled={isInvoiceDisabled}
          className={`p-2.5 rounded-full transition-all duration-200 group ${
            hasInvoice
              ? "border border-blue-200 bg-blue-50 text-blue-600 hover:bg-blue-100 hover:text-blue-700 hover:scale-110"
              : "bg-slate-100 text-slate-400 cursor-not-allowed"
          }`}
          title={
            hasInvoice
              ? "Download Invoice"
              : "Available once invoice is ready"
          }
        >
          {downloadingInvoice[order.id] ? (
            <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
          ) : (
            <FaFileInvoiceDollar className="w-5 h-5" />
          )}
          <div className="invisible group-hover:visible absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 text-xs text-white bg-slate-800 rounded whitespace-nowrap">
            {hasInvoice
              ? "Download Invoice"
              : "Available once invoice is ready"}
          </div>
        </button>

        <button
          onClick={(e) => {
            e.stopPropagation();
            if (isPaid) onDownloadReceipt(order.id);
          }}
          disabled={isReceiptDisabled}
          className={`p-2.5 rounded-full transition-all duration-200 group ${
            isPaid
              ? "border border-blue-200 bg-blue-50 text-blue-600 hover:bg-blue-100 hover:text-blue-700 hover:scale-110"
              : "bg-slate-100 text-slate-400 cursor-not-allowed"
          }`}
          title={isPaid ? "Download Receipt" : "Available after payment"}
        >
          {downloadingReceipt[order.id] ? (
            <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
          ) : (
            <FaFileAlt className="w-5 h-5" />
          )}
          <div className="invisible group-hover:visible absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 text-xs text-white bg-slate-800 rounded whitespace-nowrap">
            {isPaid ? "Download Receipt" : "Available after payment"}
          </div>
        </button>

        <button
          onClick={(e) => {
            e.stopPropagation();
            if (isPaid) onDownloadDocuments(order.id);
          }}
          disabled={isDocumentsDisabled}
          className={`p-2.5 rounded-full transition-all duration-200 group relative ${
            isPaid
              ? "border border-blue-200 bg-blue-50 text-blue-600 hover:bg-blue-100 hover:text-blue-700 hover:scale-110"
              : "bg-slate-100 text-slate-400 cursor-not-allowed"
          }`}
          title={isPaid ? "Download Documents" : "Available after payment"}
        >
          <FaFilePdf className="w-5 h-5" />
          <div className="invisible group-hover:visible absolute bottom-full right-0 mb-2 px-2 py-1 text-xs text-white bg-slate-800 rounded whitespace-nowrap">
            {isPaid ? "Download Documents" : "Available after payment"}
          </div>
        </button>

        <button
          onClick={(e) => {
            e.stopPropagation();
            if (canArchive) onArchive(order);
          }}
          disabled={!canArchive}
          className={`p-2.5 rounded-full transition-all duration-200 group relative ${
            canArchive
              ? "border border-red-200 bg-red-50 text-red-600 hover:bg-red-100 hover:text-red-700 hover:scale-110"
              : "bg-slate-100 text-slate-400 cursor-not-allowed"
          }`}
          title={
            isPaid
              ? "Paid orders cannot be archived"
              : archiveDisabled
                ? "Unavailable while invoice/payment is in progress"
                : "Archive order"
          }
        >
          <ArchiveBoxIcon className="w-5 h-5" />
          <div className="invisible group-hover:visible absolute bottom-full right-0 mb-2 px-2 py-1 text-xs text-white bg-slate-800 rounded whitespace-nowrap">
            {isPaid
              ? "Paid orders cannot be archived"
              : archiveDisabled
                ? "Unavailable while invoice/payment is in progress"
                : "Archive order"}
          </div>
        </button>
      </div>

      {/* Mobile (<768px): ⋮ dropdown menu */}
      <div className="relative flex justify-center md:hidden" ref={menuRef}>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setIsOpen((current) => !current);
          }}
          className={`inline-flex h-10 w-10 items-center justify-center rounded-full border transition ${
            isOpen
              ? "border-blue-200 bg-blue-50 text-blue-700"
              : "border-slate-200 bg-white text-slate-600 hover:border-blue-200 hover:bg-blue-50 hover:text-blue-700"
          }`}
          aria-haspopup="menu"
          aria-expanded={isOpen}
          aria-label="Open actions menu"
          title="Actions"
        >
          <EllipsisVerticalIcon className="h-5 w-5" />
        </button>

        {isOpen && (
          <div
            role="menu"
            className="absolute right-0 top-full z-40 mt-2 w-56 overflow-hidden rounded-2xl border border-slate-200 bg-white py-1.5 shadow-xl shadow-slate-200/70"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              role="menuitem"
              onClick={(e) => {
                e.stopPropagation();
                if (isInvoiceDisabled) return;
                onDownloadInvoice(order.id);
                closeMenu();
              }}
              disabled={isInvoiceDisabled}
              className={`flex w-full items-center gap-3 px-3.5 py-2.5 text-left text-sm transition ${
                isInvoiceDisabled
                  ? "cursor-not-allowed text-slate-400"
                  : "text-slate-700 hover:bg-blue-50 hover:text-blue-700"
              }`}
              title={
                hasInvoice
                  ? "Download Invoice"
                  : "Available once invoice is ready"
              }
            >
              {downloadingInvoice[order.id] ? (
                <div className="h-4 w-4 shrink-0 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
              ) : (
                <FaFileInvoiceDollar className="h-4 w-4 shrink-0" />
              )}
              <span className="min-w-0 flex-1">
                Download Invoice
                {!hasInvoice && (
                  <span className="mt-0.5 block text-[11px] text-slate-400">Available once invoice is ready</span>
                )}
              </span>
            </button>

            <button
              type="button"
              role="menuitem"
              onClick={(e) => {
                e.stopPropagation();
                if (isReceiptDisabled) return;
                onDownloadReceipt(order.id);
                closeMenu();
              }}
              disabled={isReceiptDisabled}
              className={`flex w-full items-center gap-3 px-3.5 py-2.5 text-left text-sm transition ${
                isReceiptDisabled
                  ? "cursor-not-allowed text-slate-400"
                  : "text-slate-700 hover:bg-blue-50 hover:text-blue-700"
              }`}
              title={isPaid ? "Download Receipt" : "Available after payment"}
            >
              {downloadingReceipt[order.id] ? (
                <div className="h-4 w-4 shrink-0 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
              ) : (
                <FaFileAlt className="h-4 w-4 shrink-0" />
              )}
              <span className="min-w-0 flex-1">
                Download Receipt
                {!isPaid && (
                  <span className="mt-0.5 block text-[11px] text-slate-400">Available after payment</span>
                )}
              </span>
            </button>

            <button
              type="button"
              role="menuitem"
              onClick={(e) => {
                e.stopPropagation();
                if (isDocumentsDisabled) return;
                onDownloadDocuments(order.id);
                closeMenu();
              }}
              disabled={isDocumentsDisabled}
              className={`flex w-full items-center gap-3 px-3.5 py-2.5 text-left text-sm transition ${
                isDocumentsDisabled
                  ? "cursor-not-allowed text-slate-400"
                  : "text-slate-700 hover:bg-blue-50 hover:text-blue-700"
              }`}
              title={isPaid ? "Download Documents" : "Available after payment"}
            >
              <FaFilePdf className="h-4 w-4 shrink-0" />
              <span className="min-w-0 flex-1">
                Download Documents
                {!isPaid && (
                  <span className="mt-0.5 block text-[11px] text-slate-400">Available after payment</span>
                )}
              </span>
            </button>

            <button
              type="button"
              role="menuitem"
              onClick={(e) => {
                e.stopPropagation();
                if (!canArchive) return;
                onArchive(order);
                closeMenu();
              }}
              disabled={!canArchive}
              className={`flex w-full items-center gap-3 px-3.5 py-2.5 text-left text-sm transition ${
                !canArchive
                  ? "cursor-not-allowed text-slate-400"
                  : "text-red-700 hover:bg-red-50"
              }`}
              title={
                isPaid
                  ? "Paid orders cannot be archived"
                  : archiveDisabled
                    ? "Unavailable while invoice/payment is in progress"
                    : "Archive order"
              }
            >
              <ArchiveBoxIcon className="h-4 w-4 shrink-0" />
              <span className="min-w-0 flex-1">Archive order</span>
            </button>
          </div>
        )}
      </div>
    </>
  );
};

// Update the StatusBadge component to include the Pay Now button
const StatusBadge = ({ status, orderId, onPayNow }) => {
  // Unpaid orders surface an actionable Pay Now button instead of a plain status pill.
  const isPending = status.toLowerCase() === "payment pending";

  if (isPending) {
    return (
      <button
        onClick={(e) => {
          e.stopPropagation();
          onPayNow(orderId);
        }}
        className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-2.5 py-1.5 text-xs font-medium text-white transition hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 min-[425px]:px-4 min-[425px]:py-2 min-[425px]:text-sm"
      >
        <span className="min-[425px]:hidden">Pay</span>
        <span className="hidden min-[425px]:inline">Pay Now</span>
      </button>
    );
  }

  const getStatusStyles = () => {
    switch (status.toLowerCase()) {
      case "paid":
        return "bg-emerald-100 text-emerald-700 border-emerald-200";
      case "payment pending":
        return "bg-violet-100 text-violet-700 border-violet-200";
      case "processing":
        return "bg-slate-100 text-slate-600 border-slate-200";
      default:
        return "bg-slate-100 text-slate-600 border-slate-200";
    }
  };

  const isProcessing = status.toLowerCase() === "processing";

  return (
    <span
      className={`inline-block max-w-[4.75rem] whitespace-normal break-words rounded-full border px-2 py-1 text-left text-xs font-semibold leading-snug min-[425px]:max-w-none min-[425px]:whitespace-nowrap ${getStatusStyles()}`}
    >
      {isProcessing && (
        <span className="mr-1 inline-block h-3 w-3 animate-spin rounded-full border-2 border-slate-300 border-t-slate-600 align-[-2px]"></span>
      )}
      {status
        .split("_")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ")}
    </span>
  );
};

// Stuck-Processing recovery: a few spaced send-pending-invoices calls per page load,
// plus status polling while orders are Processing (bounded to ~5 minutes).
const MAX_INVOICE_RECOVERY_ATTEMPTS = 3;
const INVOICE_RECOVERY_RETRY_MS = 30_000;
const PROCESSING_POLL_MS = 5_000;
const MAX_PROCESSING_RECHECKS = 60;

const CustomerDashboard = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [highlightedOrderId, setHighlightedOrderId] = useState(null);
  const orderRefs = useRef({});
  const pendingInvoiceRecoveryRef = useRef(false);
  const invoiceRecoveryAttemptsRef = useRef(0);
  const lastInvoiceRecoveryAtRef = useRef(0);
  const invoiceRecoveryBackgroundRef = useRef(false);
  const processingRechecksRef = useRef(0);
  const processingRecheckTimeoutRef = useRef(null);
  const [isRefreshingOrders, setIsRefreshingOrders] = useState(false);
  const { user } = useAuth();
  const [downloadingInvoice, setDownloadingInvoice] = useState({});
  const [downloadingReceipt, setDownloadingReceipt] = useState({});
  const [currentPage, setCurrentPage] = useState(readStoredDashboardPage);
  const [ordersPerPage] = useState(50);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [filterField, setFilterField] = useState('');
  const [filterValue, setFilterValue] = useState('');
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [archiveTarget, setArchiveTarget] = useState(null);
  const [isArchiving, setIsArchiving] = useState(false);
  const [toast, setToast] = useState(null);
  const toastTimeoutRef = useRef(null);

  const searchFieldOptions = [
    { value: 'mre_number', label: 'MRE Approval' },
    { value: 'unique_id', label: 'Unique ID' },
    { value: 'chassis_vin', label: 'VIN/Chassis' },
    { value: 'make', label: 'Make' },
    { value: 'model', label: 'Model' },
    { value: 'model_code', label: 'Model Code' },
  ];

  // Drops case and all whitespace so "land cruiser" still matches "LandCruiser".
  const normalizeFilterText = (value) =>
    String(value || '')
      .toLowerCase()
      .replace(/\s+/g, '')
      .trim();

  // Reads ?highlightOrderId from the URL; returns null once the page is hard-refreshed.
  const highlightOrderId = useHighlightOrderId('/dashboard');

  useEffect(() => {
    window.sessionStorage.setItem(CUSTOMER_DASHBOARD_PAGE_KEY, String(currentPage));
  }, [currentPage]);

  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(filteredOrders.length / ordersPerPage) || 1);
    if (currentPage > maxPage) {
      setCurrentPage(maxPage);
    }
  }, [filteredOrders.length, ordersPerPage, currentPage]);

  // Hard refresh restores browser scroll mid-page; keep the saved page but pin viewport to top
  // unless we are actively highlighting an order from in-app navigation.
  useLayoutEffect(() => {
    if (highlightOrderId || !isDocumentReload()) {
      return undefined;
    }

    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }

    const scrollTop = () => {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    };

    scrollTop();
    const immediateId = window.setTimeout(scrollTop, 0);
    const delayedId = window.setTimeout(scrollTop, 120);

    return () => {
      window.clearTimeout(immediateId);
      window.clearTimeout(delayedId);
    };
  }, [highlightOrderId]);

  // Content height changes after fetch; re-pin to top on reload once loading settles.
  useEffect(() => {
    if (highlightOrderId || loading || !isDocumentReload()) {
      return;
    }

    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [loading, highlightOrderId]);

  useEffect(() => {
    if (user) {
      pendingInvoiceRecoveryRef.current = false;
      invoiceRecoveryAttemptsRef.current = 0;
      lastInvoiceRecoveryAtRef.current = 0;
      invoiceRecoveryBackgroundRef.current = false;
      processingRechecksRef.current = 0;
      if (processingRecheckTimeoutRef.current) {
        window.clearTimeout(processingRecheckTimeoutRef.current);
        processingRecheckTimeoutRef.current = null;
      }
      fetchOrders(user.id, true);
      return;
    }
  }, [user]);

  useEffect(() => {
    if (!highlightOrderId || filteredOrders.length === 0) {
      return;
    }

    const targetIndex = filteredOrders.findIndex(
      (order) => String(order.id) === String(highlightOrderId)
    );

    if (targetIndex === -1) {
      return;
    }

    const targetOrder = filteredOrders[targetIndex];
    const targetPage = Math.floor(targetIndex / ordersPerPage) + 1;

    setCurrentPage((prevPage) => (prevPage === targetPage ? prevPage : targetPage));
    setExpandedRows((prev) => {
      const next = new Set(prev);
      next.add(targetOrder.id);
      return next;
    });
    setHighlightedOrderId(targetOrder.id);
  }, [highlightOrderId, filteredOrders, ordersPerPage]);

  // Scroll the flagged row into view, then clear the highlight after a few seconds.
  useEffect(() => {
    if (!highlightedOrderId) {
      return;
    }

    const targetElement = orderRefs.current[highlightedOrderId];
    if (!targetElement) {
      return;
    }

    targetElement.scrollIntoView({ behavior: 'smooth', block: 'center' });

    const timeoutId = window.setTimeout(() => {
      setHighlightedOrderId((current) => (current === highlightedOrderId ? null : current));
    }, 3500);

    return () => window.clearTimeout(timeoutId);
  }, [highlightedOrderId, currentPage]);

  // Update filtered orders when orders change
  useEffect(() => {
    const filtered = applyFilters(orders, filterField, filterValue);
    setFilteredOrders(filtered);
  }, [orders, filterField, filterValue]);

  const fetchOrders = async (userId, isInitialLoad = false) => {
    try {
      // Background refreshes (processing rechecks) keep the table visible.
      if (isInitialLoad) {
        setLoading(true);
        setIsLoading(true);
      }

      // Fetch ALL data without pagination for filtering
      const { data: ordersData, error } = await supabase
        .from("admin_submissions_view")
        .select(
          `
          *,
          *
        `
        )
        .eq("user_id", userId)
        .eq("is_archived", false)
        .order("created_at", { ascending: false });

      if (error) throw error;

      if (!ordersData || ordersData.length === 0) {
        console.warn("No Orders found for this email.");
        setOrders([]);
        setFilteredOrders([]);
        setTotalCount(0);
        setTotalPages(1);
        if (isInitialLoad) {
          setLoading(false);
        }
        return;
      }

      const formattedOrders = ordersData.map((order) => ({
        id: order.id,
        rawNumber: order.user_raw || "- -",
        mreNumber: order.mr_approval || "- -",
        // No transaction yet (rare race / invoice lag) → processing until Payment Pending/Paid.
        status: (order.payment_status || "Processing").toLowerCase(),
        submissionDate: convertToMelbourneDate(order.created_at),
        paymentDate: order.payment_timestamp
          ? convertToMelbourneDate(order.payment_timestamp)
          : "- -",
        paymentAmount: order.transaction_price
          ? `$${Number(order.transaction_price).toFixed(2)}`
          : "- -",
        userName: order.user_name || "- -",
        userEmail: order.user_email || "- -",
        // Unique ID is only revealed after payment; show the stored ID as-is when present
        customerUniqueId: (() => {
          const isPaid = (order.payment_status || "").toLowerCase() === "paid";
          if (!isPaid) return "-";
          const uniqueId = order.customer_unique_id;
          return uniqueId != null && String(uniqueId).trim() !== ""
            ? String(uniqueId)
            : "-";
        })(),
        chassisVin: `${order.vin_number || ""}` || "- -",
        chassisCode: order.chassis_code || "- -",
        buildDate: order.build_date || "- -",
        state: order.state || "- -",
        avvNumber: order.avv_approval_number || "- -",
        avvEmail: order.avv_approval_email || "- -",
        make: order.car_make || "- -",
        model: order.car_model || "- -",
        invoiceNumber: order.invoice_number || "- -"
      }));

      setOrders(formattedOrders);
      setTotalCount(formattedOrders.length);
      setTotalPages(Math.ceil(formattedOrders.length / ordersPerPage));

      // Recovery only for stuck Processing orders (older than 60s). Fresh orders are still
      // being invoiced by check-common-price — recovering immediately caused duplicate transactions.
      const STUCK_PROCESSING_MS = 60_000;
      const now = Date.now();
      const hasStuckProcessingOrders = ordersData.some((order) => {
        const status = (order.payment_status || "Processing").toLowerCase();
        if (status !== "processing") return false;
        const createdAtMs = order.created_at ? new Date(order.created_at).getTime() : 0;
        return createdAtMs > 0 && now - createdAtMs >= STUCK_PROCESSING_MS;
      });
      const hasProcessingOrders = formattedOrders.some((order) => order.status === "processing");
      if (!hasProcessingOrders) {
        invoiceRecoveryBackgroundRef.current = false;
      }

      // While send-pending-invoices is still chaining in the background, only refresh statuses:
      // a second recovery call would race the chain and could duplicate Xero invoices.
      const recoveryCooldownLeft =
        lastInvoiceRecoveryAtRef.current + INVOICE_RECOVERY_RETRY_MS - now;
      const canRecover =
        hasStuckProcessingOrders &&
        !pendingInvoiceRecoveryRef.current &&
        !invoiceRecoveryBackgroundRef.current &&
        invoiceRecoveryAttemptsRef.current < MAX_INVOICE_RECOVERY_ATTEMPTS &&
        recoveryCooldownLeft <= 0;
      const shouldKeepWatching =
        hasProcessingOrders &&
        processingRechecksRef.current < MAX_PROCESSING_RECHECKS &&
        (!hasStuckProcessingOrders ||
          invoiceRecoveryBackgroundRef.current ||
          invoiceRecoveryAttemptsRef.current < MAX_INVOICE_RECOVERY_ATTEMPTS);

      if (canRecover) {
        pendingInvoiceRecoveryRef.current = true;
        invoiceRecoveryAttemptsRef.current += 1;
        lastInvoiceRecoveryAtRef.current = now;
        void (async () => {
          try {
            const { data: sessionData } = await supabase.auth.getSession();
            const accessToken = sessionData?.session?.access_token;
            const response = await fetch(
              `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/send-pending-invoices`,
              {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                  ...(accessToken
                    ? { Authorization: `Bearer ${accessToken}` }
                    : {}),
                },
                body: JSON.stringify({ user_id: userId }),
              }
            );
            const result = await response.json().catch(() => ({}));
            invoiceRecoveryBackgroundRef.current = response.ok && !!result.in_background;
          } catch (recoveryError) {
            console.error("Pending invoice recovery failed:", recoveryError);
          } finally {
            window.setTimeout(() => {
              pendingInvoiceRecoveryRef.current = false;
              void fetchOrders(userId, false);
            }, 4000);
          }
        })();
      } else if (
        !pendingInvoiceRecoveryRef.current &&
        shouldKeepWatching &&
        !processingRecheckTimeoutRef.current
      ) {
        // Poll statuses so a finished invoice shows up promptly; recovery stays gated above.
        processingRechecksRef.current += 1;
        processingRecheckTimeoutRef.current = window.setTimeout(() => {
          processingRecheckTimeoutRef.current = null;
          void fetchOrders(userId, false);
        }, PROCESSING_POLL_MS);
      }
    } catch (error) {
      console.error("Error fetching orders:", error);
    } finally {
      if (isInitialLoad) {
        setLoading(false);
      }
      setIsLoading(false);
    }
  };

  // Manual reload: fetches fresh statuses without blanking the table and re-arms polling/recovery.
  const handleRefreshOrders = async () => {
    if (!user || isRefreshingOrders) return;
    setIsRefreshingOrders(true);
    processingRechecksRef.current = 0;
    invoiceRecoveryAttemptsRef.current = 0;
    try {
      await fetchOrders(user.id, false);
    } finally {
      setIsRefreshingOrders(false);
    }
  };

  const applyFilters = (ordersToFilter, currentFilterField, currentFilterValue) => {
    if (!currentFilterField || !currentFilterValue || !currentFilterValue.trim()) {
      return ordersToFilter;
    }

    const filterValue = currentFilterValue.toLowerCase().trim();
    const normalizedFilterValue = normalizeFilterText(currentFilterValue);

    return ordersToFilter.filter(order => {
      switch (currentFilterField) {
        case 'mre_number':
          return order.mreNumber.toLowerCase().includes(filterValue);
        case 'unique_id':
          return order.customerUniqueId.toLowerCase().includes(filterValue);
        case 'chassis_vin':
          return order.chassisVin.toLowerCase().includes(filterValue);
        case 'make':
          return normalizeFilterText(order.make).includes(normalizedFilterValue);
        case 'model':
          return normalizeFilterText(order.model).includes(normalizedFilterValue);
        case 'model_code':
          return normalizeFilterText(order.chassisCode).includes(normalizedFilterValue);
        default:
          return true;
      }
    });
  };

  const handleFilterValueChange = (value) => {
    setFilterValue(value);
    setCurrentPage(1);
    setFilteredOrders(applyFilters(orders, filterField, value));
  };

  // Switching the search field clears the term and returns to the unfiltered list.
  const handleFilterFieldChange = (field) => {
    setFilterField(field);
    setFilterValue('');
    setCurrentPage(1);
    setFilteredOrders(orders);
  };

  // Navigates to the cart with this order pre-selected for checkout.
  const handlePayNow = (orderId) => {
    navigate('/cart', { state: { preSelectedOrderId: orderId } });
  };

  // Downloads the invoice PDF out of the "users" bucket, which stores it under the
  // `<user id>/invoices/<invoice number>.pdf` convention.
  const handleDownloadInvoice = async (orderId) => {
    try {
      // Set loading state for this specific invoice
      setDownloadingInvoice((prev) => ({ ...prev, [orderId]: true }));

      // First, get the invoice number for this order
      const order = orders.find((o) => o.id === orderId);
      if (!order?.invoiceNumber) {
        throw new Error("Invoice number not found");
      }

      // Construct the storage path
      const storagePath = `${user.id}/invoices/${order.invoiceNumber}.pdf`;

      // Get the file from storage
      const { data, error } = await supabase.storage
        .from("users")
        .download(storagePath);

      if (error) {
        throw error;
      }

      // Create a blob from the data
      const blob = new Blob([data], { type: "application/pdf" });

      // Create a URL for the blob
      const url = window.URL.createObjectURL(blob);

      // Create a temporary link element
      const link = document.createElement("a");
      link.href = url;
      link.download = `invoice_${order.invoiceNumber}.pdf`;

      // Append to document, click, and cleanup
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading invoice:", error);
      // You might want to add a toast notification here
      alert("Failed to download invoice. Please try again later.");
    } finally {
      // Clear loading state for this specific invoice
      setDownloadingInvoice((prev) => ({ ...prev, [orderId]: false }));
    }
  };

  // Receipts are served from Order History, so link across with the order highlighted.
  const handleDownloadReceipt = async (orderId) => {
    navigate(`/order-history?highlightOrderId=${encodeURIComponent(orderId)}`);
  };

  // Downloads the report files for the specified order.
  const handleDownloadDocuments = (orderId) => {
    navigate(`/download-report-files/${orderId}`);
  };

  const showToast = (title, message, tone = 'success') => {
    if (toastTimeoutRef.current) {
      clearTimeout(toastTimeoutRef.current);
    }
    setToast({ title, message, tone });
    toastTimeoutRef.current = setTimeout(() => {
      setToast(null);
      toastTimeoutRef.current = null;
    }, 7000);
  };

  const isArchiveDisabledForOrder = (order) => {
    if (!canArchiveOrderStatus(order.status)) return true;
    // Block while invoice recovery / processing polling is in flight for Processing rows.
    if (order.status === 'processing') {
      return (
        pendingInvoiceRecoveryRef.current ||
        invoiceRecoveryBackgroundRef.current
      );
    }
    return false;
  };

  const handleArchiveClick = (order) => {
    if (isArchiveDisabledForOrder(order)) return;
    setArchiveTarget(order);
  };

  const handleArchiveConfirm = async (reason) => {
    if (!archiveTarget?.id || !user?.id) return;
    setIsArchiving(true);
    try {
      await archiveOrder(archiveTarget.id, reason);
      setArchiveTarget(null);
      showToast('Order archived!', 'It was moved to Archived Orders. The VIN is now available for new orders.');
      window.dispatchEvent(new Event('customer-cart-updated'));
      await fetchOrders(user.id, false);
    } catch (err) {
      showToast('Archive failed', err.message || 'Could not archive order', 'error');
    } finally {
      setIsArchiving(false);
    }
  };

  // Toggles the expanded/collapsed state of a row in the orders table.
  const toggleRow = (orderId) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(orderId)) {
        newSet.delete(orderId);
      } else {
        newSet.add(orderId);
      }
      return newSet;
    });
  };

  // Add pagination controls component
  const PaginationControls = () => {
    const pageNumbers = [];
    const maxVisiblePages = 5;
    
    // Use filtered orders for pagination
    const currentTotalPages = Math.ceil(filteredOrders.length / ordersPerPage);
    const currentTotalCount = filteredOrders.length;
    
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(currentTotalPages, startPage + maxVisiblePages - 1);
    
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    const indexOfLastOrder = currentPage * ordersPerPage;
    const indexOfFirstOrder = indexOfLastOrder - ordersPerPage;

    if (currentTotalPages <= 1) return null;

    return (
      <div className="flex items-center justify-between border-t border-slate-200 bg-white px-4 py-3 sm:px-6">
        <div className="flex flex-1 justify-between sm:hidden">
          <button
            type="button"
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
            className="relative inline-flex items-center rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >
            Previous
          </button>
          <button
            type="button"
            onClick={() => setCurrentPage(prev => Math.min(prev + 1, currentTotalPages))}
            disabled={currentPage === currentTotalPages}
            className="relative ml-3 inline-flex items-center rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >
            Next
          </button>
        </div>
        <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
          <p className="text-sm text-slate-700">
            Showing <span className="font-medium">{indexOfFirstOrder + 1}</span> to{' '}
            <span className="font-medium">
              {Math.min(indexOfLastOrder, currentTotalCount)}
            </span> of{' '}
            <span className="font-medium">{currentTotalCount}</span> results
          </p>
          <nav className="inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
            <button
              type="button"
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="relative inline-flex items-center rounded-l-[0.75rem] border border-slate-300 bg-white px-2 py-2 text-slate-400 hover:bg-slate-50 disabled:opacity-50"
            >
              <span className="sr-only">Previous</span>
              <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" />
            </button>

            {startPage > 1 && (
              <>
                <button
                  type="button"
                  onClick={() => setCurrentPage(1)}
                  className="relative inline-flex items-center border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                >
                  1
                </button>
                {startPage > 2 && (
                  <span className="relative inline-flex items-center border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700">
                    ...
                  </span>
                )}
              </>
            )}

            {pageNumbers.map(number => (
              <button
                key={number}
                type="button"
                onClick={() => setCurrentPage(number)}
                className={`relative inline-flex items-center border px-4 py-2 text-sm font-medium ${
                  currentPage === number
                    ? 'z-10 border-blue-500 bg-blue-50 text-blue-600'
                    : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50'
                }`}
              >
                {number}
              </button>
            ))}

            {endPage < currentTotalPages && (
              <>
                {endPage < currentTotalPages - 1 && (
                  <span className="relative inline-flex items-center border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700">
                    ...
                  </span>
                )}
                <button
                  type="button"
                  onClick={() => setCurrentPage(currentTotalPages)}
                  className="relative inline-flex items-center border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                >
                  {currentTotalPages}
                </button>
              </>
            )}

            <button
              type="button"
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, currentTotalPages))}
              disabled={currentPage === currentTotalPages}
              className="relative inline-flex items-center rounded-r-[0.75rem] border border-slate-300 bg-white px-2 py-2 text-slate-400 hover:bg-slate-50 disabled:opacity-50"
            >
              <span className="sr-only">Next</span>
              <ChevronRightIcon className="h-5 w-5" aria-hidden="true" />
            </button>
          </nav>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        Loading...
      </div>
    );
  }

  return (
    <CustomerLayout
      activeItemId="dashboard"
      title="My Dashboard"
      subtitle="View and manage your model report orders."
      contentClassName="mx-auto w-full max-w-none"
    >
        <ArchiveConfirmModal
          open={!!archiveTarget}
          orderLabel={archiveTarget ? `${archiveTarget.chassisVin || 'Order'} · ${archiveTarget.make || ''} ${archiveTarget.model || ''}`.trim() : ''}
          loading={isArchiving}
          onCancel={() => !isArchiving && setArchiveTarget(null)}
          onConfirm={handleArchiveConfirm}
        />

        <div className="mb-6 rounded-[2rem] border border-slate-200 bg-white p-4 shadow-sm sm:p-6">
          <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
            <div className="grid flex-1 gap-4 lg:grid-cols-2 xl:grid-cols-[minmax(0,1.3fr)_minmax(0,0.9fr)]">
              <div className="relative">
                {/* The search box stays locked until a field is chosen in the adjacent select */}
                <input
                  type="text"
                  value={filterValue}
                  onChange={(e) => handleFilterValueChange(e.target.value)}
                  placeholder={
                    filterField
                      ? `Search by ${searchFieldOptions.find((field) => field.value === filterField)?.label.toLowerCase()}...`
                      : 'Select a field to search...'
                  }
                  disabled={!filterField}
                  className={`w-full rounded-2xl border px-11 py-3 text-sm shadow-sm transition focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100 ${!filterField ? 'cursor-not-allowed border-slate-200 bg-slate-100 text-slate-500' : 'border-slate-300 bg-white text-slate-900'}`}
                />
                <svg
                  className={`absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 ${!filterField ? 'text-slate-300' : 'text-slate-400'}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>

              <select
                value={filterField}
                onChange={(e) => handleFilterFieldChange(e.target.value)}
                className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 shadow-sm transition focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
              >
                <option value="">Select Search Field</option>
                {searchFieldOptions.map((field) => (
                  <option key={field.value} value={field.value}>
                    {field.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-sm">
          <div className="flex items-center justify-between gap-3 px-6 py-4 border-b border-gray-200">
            <h2 className="text-md font-medium text-slate-900 uppercase tracking-[0.04em]">My Model Reports</h2>
            <button
              type="button"
              onClick={handleRefreshOrders}
              disabled={isRefreshingOrders || isLoading}
              className="inline-flex items-center gap-2 rounded-xl border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 shadow-sm transition hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-100 disabled:cursor-not-allowed disabled:opacity-60 min-[425px]:text-sm"
              title="Reload orders"
            >
              <svg
                className={`h-4 w-4 ${isRefreshingOrders ? 'animate-spin' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
              {isRefreshingOrders ? 'Refreshing...' : 'Refresh'}
            </button>
          </div>

          {/* Spinner, then either an empty state (never ordered vs. no search match) or the table */}
          {isLoading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
              <p className="mt-2 text-sm text-gray-500">Loading orders...</p>
            </div>
          ) : filteredOrders.length === 0 ? (
            orders.length > 0 ? <NoMatchingOrdersDisplay /> : <NoOrdersDisplay />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full table-auto divide-y divide-gray-200 bg-white text-xs min-[425px]:text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="w-16 px-4 py-4 text-left text-[11px] font-medium uppercase tracking-wider text-gray-500 min-[425px]:text-xs">
                      
                    </th>
                    <th className="hidden w-16 px-4 py-4 text-left text-[11px] font-medium uppercase tracking-wider text-gray-500 min-[425px]:text-xs xl:table-cell">
                      S No.
                    </th>
                    <th className="hidden w-36 px-4 py-4 text-left text-[11px] font-medium uppercase tracking-wider text-gray-500 min-[425px]:text-xs xl:table-cell">
                      MRE Approval
                    </th>
                    <th className="hidden w-36 px-4 py-4 text-left text-[11px] font-medium uppercase tracking-wider text-gray-500 min-[425px]:text-xs md:table-cell">
                      Unique ID
                    </th>
                    <th className="w-40 px-4 py-4 text-left text-[11px] font-medium uppercase tracking-wider text-gray-500 min-[425px]:text-xs">
                      VIN/Chassis
                    </th>
                    <th className="hidden w-28 px-4 py-4 text-left text-[11px] font-medium uppercase tracking-wider text-gray-500 min-[425px]:text-xs 2xl:table-cell">
                      Amount
                    </th>
                    <th className="w-32 px-4 py-4 text-left text-[11px] font-medium uppercase tracking-wider text-gray-500 min-[425px]:text-xs">
                      Status
                    </th>
                    <th className="w-20 px-4 py-4 text-center text-[11px] font-medium uppercase tracking-wider text-gray-500 min-[425px]:text-xs md:w-40">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredOrders
                    .slice((currentPage - 1) * ordersPerPage, currentPage * ordersPerPage)
                    .map((order, index) => {
                    const isHighlighted = highlightedOrderId === order.id;

                    return (
                    <React.Fragment key={order.id}>
                      <tr
                        ref={(element) => {
                          if (element) {
                            orderRefs.current[order.id] = element;
                            return;
                          }
                          delete orderRefs.current[order.id];
                        }}
                        className={`cursor-pointer transition-colors hover:bg-gray-50 ${
                          isHighlighted ? 'bg-blue-50 ring-2 ring-inset ring-blue-400' : ''
                        }`}
                        onClick={() => toggleRow(order.id)}
                      >
                        <td className="px-4 py-4 whitespace-nowrap">
                          <button
                            type="button"
                            className="text-gray-500 hover:text-gray-700 focus:outline-none"
                            tabIndex={-1}
                          >
                            {expandedRows.has(order.id) ? (
                              <ChevronUpIcon className="h-5 w-5" />
                            ) : (
                              <ChevronDownIcon className="h-5 w-5" />
                            )}
                          </button>
                        </td>
                        <td className="hidden px-4 py-4 whitespace-nowrap text-xs min-[425px]:text-sm xl:table-cell">
                          {/* Row numbering continues across pages rather than restarting at 1 */}
                          {(currentPage - 1) * ordersPerPage + index + 1}
                        </td>
                        <td className="hidden px-4 py-4 whitespace-nowrap text-xs min-[425px]:text-sm xl:table-cell">
                          <CopyableText value={order.mreNumber} />
                        </td>
                        <td className="hidden px-4 py-4 whitespace-nowrap text-xs min-[425px]:text-sm md:table-cell">
                          <CopyableText value={order.customerUniqueId} />
                        </td>
                        <td className="max-w-[8.5rem] px-3 py-4 text-xs min-[425px]:text-sm sm:max-w-none sm:px-4 sm:whitespace-nowrap">
                          <CopyableText
                            value={order.chassisVin}
                            className="break-all whitespace-normal sm:break-normal sm:whitespace-nowrap"
                          />
                        </td>
                        <td className="hidden px-4 py-4 whitespace-nowrap text-xs min-[425px]:text-sm 2xl:table-cell">
                          {order.paymentAmount}
                        </td>
                        <td className="px-2 py-4 text-xs min-[425px]:px-4 min-[425px]:whitespace-nowrap min-[425px]:text-sm">
                          <StatusBadge
                            status={order.status}
                            orderId={order.id}
                            onPayNow={handlePayNow}
                          />
                        </td>
                        <td className="px-2 py-4 text-center whitespace-nowrap min-[425px]:px-4">
                          <OrderActions
                            order={order}
                            onDownloadInvoice={handleDownloadInvoice}
                            onDownloadReceipt={handleDownloadReceipt}
                            onDownloadDocuments={handleDownloadDocuments}
                            onArchive={handleArchiveClick}
                            archiveDisabled={isArchiveDisabledForOrder(order)}
                            downloadingInvoice={downloadingInvoice}
                            downloadingReceipt={downloadingReceipt}
                          />
                        </td>
                      </tr>
                      {expandedRows.has(order.id) && (
                        <tr className="bg-slate-50/70">
                          <td colSpan="8" className="px-5 py-5 lg:px-6">
                            <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_18px_40px_-28px_rgba(15,23,42,0.35)]">
                              <div className="grid grid-cols-1 gap-4 p-4 lg:grid-cols-2 lg:p-5">
                                <div className="rounded-xl border border-slate-200 bg-orange-50/80">
                                  <div className="border-b border-slate-100 bg-slate-50/80 px-4 py-3">
                                    <h3 className="text-xs font-semibold uppercase tracking-[0.08em] text-orange-700 flex items-center gap-2">
                                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                      </svg>
                                      Order Details
                                    </h3>
                                  </div>
                                  <div className="grid gap-0 px-4 py-4 md:grid-cols-2">
                                    <div className="space-y-1 border-b border-slate-100 pb-4 md:border-b-0 md:border-r md:pr-4">
                                      <div className="text-[11px] font-medium uppercase tracking-[0.08em] text-slate-400">Order Date</div>
                                      <div className="mt-1 text-xs min-[425px]:text-sm text-slate-800">
                                        <CopyableText value={order.submissionDate || '-'} />
                                      </div>
                                    </div>
                                    <div className="space-y-1 border-b border-slate-100 py-4 md:border-b-0 md:px-4 md:pt-0">
                                      <div className="text-[11px] font-medium uppercase tracking-[0.08em] text-slate-400">Make</div>
                                      <div className="mt-1 text-xs min-[425px]:text-sm text-slate-800">
                                        <CopyableText value={order.make || '-'} />
                                      </div>
                                    </div>
                                    <div className="space-y-1 border-b border-slate-100 py-4 md:border-b-0 md:border-t md:border-slate-100 md:pr-4 md:pt-4">
                                      <div className="text-[11px] font-medium uppercase tracking-[0.08em] text-slate-400">Model</div>
                                      <div className="mt-1 text-xs min-[425px]:text-sm text-slate-800">
                                        <CopyableText value={order.model || '-'} />
                                      </div>
                                    </div>
                                    <div className="space-y-1 border-b border-slate-100 py-4 md:border-b-0 md:border-l md:border-t md:border-slate-100 md:pl-4 md:pt-4">
                                      <div className="text-[11px] font-medium uppercase tracking-[0.08em] text-slate-400">Model Code</div>
                                      <div className="mt-1 text-xs min-[425px]:text-sm text-slate-800 break-all">
                                        <CopyableText value={order.chassisCode || '-'} />
                                      </div>
                                    </div>
                                    <div className="space-y-1 border-b border-slate-100 py-4 md:border-b-0 md:border-t md:border-slate-100 md:pr-4 md:pt-4">
                                      <div className="text-[11px] font-medium uppercase tracking-[0.08em] text-slate-400">Build Date</div>
                                      <div className="mt-1 text-xs min-[425px]:text-sm text-slate-800">
                                        <CopyableText value={order.buildDate || '-'} />
                                      </div>
                                    </div>
                                    <div className="space-y-1 border-b border-slate-100 py-4 md:border-b-0 md:border-l md:border-t md:border-slate-100 md:pl-4 md:pt-4">
                                      <div className="text-[11px] font-medium uppercase tracking-[0.08em] text-slate-400">AVV No</div>
                                      <div className="mt-1 text-xs min-[425px]:text-sm text-slate-800">
                                        <CopyableText value={order.avvNumber || '-'} />
                                      </div>
                                    </div>
                                    <div className="space-y-1 border-b border-slate-100 py-4 md:border-t md:border-slate-100 md:pr-4 md:pt-4 xl:border-b-0">
                                      <div className="text-[11px] font-medium uppercase tracking-[0.08em] text-slate-400">AVV Email</div>
                                      <div className="mt-1 text-xs min-[425px]:text-sm text-slate-800 break-all">
                                        <CopyableText value={order.avvEmail || '-'} />
                                      </div>
                                    </div>
                                    <div className="space-y-1 border-b border-slate-100 py-4 md:border-l md:border-t md:border-slate-100 md:pl-4 md:pt-4 xl:border-b-0">
                                      <div className="text-[11px] font-medium uppercase tracking-[0.08em] text-slate-400">State</div>
                                      <div className="mt-1 text-xs min-[425px]:text-sm text-slate-800">
                                        <CopyableText value={order.state || '-'} />
                                      </div>
                                    </div>
                                    <div className="space-y-1 border-b border-slate-100 py-4 md:border-b-0 md:border-t md:border-slate-100 md:pr-4 md:pt-4 xl:hidden">
                                      <div className="text-[11px] font-medium uppercase tracking-[0.08em] text-slate-400">MRE Approval</div>
                                      <div className="mt-1 text-xs min-[425px]:text-sm text-slate-800">
                                        <CopyableText value={order.mreNumber || '-'} />
                                      </div>
                                    </div>
                                    <div className="space-y-1 pt-4 md:hidden">
                                      <div className="text-[11px] font-medium uppercase tracking-[0.08em] text-slate-400">Unique ID</div>
                                      <div className="mt-1 text-xs min-[425px]:text-sm text-slate-800">
                                        <CopyableText value={order.customerUniqueId || '-'} />
                                      </div>
                                    </div>
                                  </div>
                                </div>

                                <div className="rounded-xl border border-slate-200 bg-emerald-50/80">
                                  <div className="border-b border-slate-100 bg-slate-50/80 px-4 py-3">
                                    <h3 className="text-xs font-semibold uppercase tracking-[0.08em] text-emerald-700 flex items-center gap-2">
                                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                                      </svg>
                                      Payment Details
                                    </h3>
                                  </div>
                                  <div className="grid gap-0 px-4 py-4 md:grid-cols-2">
                                    <div className="space-y-1 border-b border-slate-100 pb-4 md:border-r md:pr-4 2xl:border-b-0">
                                      <div className="text-[11px] font-medium uppercase tracking-[0.08em] text-slate-400">Invoice No</div>
                                      <div className="mt-1 text-xs min-[425px]:text-sm text-slate-800">
                                        <CopyableText value={order.invoiceNumber || '-'} />
                                      </div>
                                    </div>
                                    <div className="space-y-1 border-b border-slate-100 py-4 md:px-4 md:pt-0 2xl:border-b-0">
                                      <div className="text-[11px] font-medium uppercase tracking-[0.08em] text-slate-400">Payment Date</div>
                                      <div className="mt-1 text-xs min-[425px]:text-sm text-slate-800">
                                        <CopyableText value={order.paymentDate || '-'} />
                                      </div>
                                    </div>
                                    <div className="space-y-1 pt-4 md:border-t md:border-slate-100 md:pr-4 md:pt-4 2xl:hidden">
                                      <div className="text-[11px] font-medium uppercase tracking-[0.08em] text-slate-400">Amount</div>
                                      <div className="mt-1 text-xs min-[425px]:text-sm font-medium text-slate-900">
                                        <CopyableText value={order.paymentAmount || '-'} />
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
              <PaginationControls />
            </div>
          )}
        </div>

        {toast && (
          <StatusToast title={toast.title} message={toast.message} tone={toast.tone} />
        )}
    </CustomerLayout>
  );
};

export default CustomerDashboard;
